import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Lightning, MagnifyingGlass, User } from '@phosphor-icons/react';
import { ModuleShell, SkeletonBlock, EmptyState } from '../../../components/module-ui';
import { apiPath } from '../../../config/apiBase';
import { authHeaders } from '../api/ticketsApi';

const TICKETS_API = apiPath('/api/tickets');

/**
 * Full Performance view — same leaderboard as the Overview widget, larger and more detailed.
 * Opened from Performance by tickets → Full pipeline.
 */
export default function Performance360Page() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [maxClosed, setMaxClosed] = useState(1);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${TICKETS_API}/dashboard/performance`, {
      ...authHeaders(),
      params: { limit: 40 },
    }).then((res) => {
      if (cancelled) return;
      setItems(res.data?.items || []);
      setMaxClosed(res.data?.max_closed || 1);
    }).catch((e) => {
      if (!cancelled) {
        setError(e.response?.data?.detail || 'Could not load performance data');
        setItems([]);
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (loading) return undefined;
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, [loading, items]);

  const ranked = useMemo(() => {
    const term = q.trim().toLowerCase();
    let rows = [...items].sort(
      (a, b) => (b.closed || 0) - (a.closed || 0) || (b.active || 0) - (a.active || 0),
    );
    if (term) {
      rows = rows.filter((r) => {
        const blob = `${r.name || ''} ${r.email || ''} ${r.designation || ''}`.toLowerCase();
        return blob.includes(term);
      });
    }
    return rows;
  }, [items, q]);

  const totals = useMemo(() => ({
    people: items.length,
    active: items.reduce((n, r) => n + (r.active || 0), 0),
    open: items.reduce((n, r) => n + (r.open || 0), 0),
    inProgress: items.reduce((n, r) => n + (r.in_progress || 0), 0),
    closed: items.reduce((n, r) => n + (r.closed || 0), 0),
    total: items.reduce((n, r) => n + (r.total || 0), 0),
  }), [items]);

  const ceiling = Math.max(1, maxClosed, ...ranked.map((r) => r.closed || 0));

  const open360 = (userId, view = 'active') => {
    const sp = new URLSearchParams({ assigned_to: userId, view });
    navigate(`/dashboard/tickets/pipeline?${sp.toString()}`);
  };

  if (loading) {
    return (
      <ModuleShell title="Performance by tickets" subtitle="Loading…">
        <SkeletonBlock rows={10} />
      </ModuleShell>
    );
  }

  return (
    <ModuleShell
      title="Performance by tickets"
      subtitle="Full leaderboard — same view as Overview, with more detail. Click a person or 360°."
      headerActions={(
        <button type="button" className="tk-btn tk-btn-secondary" onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={16} />
          Overview
        </button>
      )}
    >
      <div data-testid="performance-360-page" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && (
          <div style={{ padding: 12, background: '#FEE2E2', color: '#B91C1C', borderRadius: 8 }}>{error}</div>
        )}

        <div className="sla-pipeline-summary" role="group" aria-label="Performance totals">
          <div className="sla-summary-chip">
            <span className="sla-summary-chip-value">{totals.people}</span>
            <span className="sla-summary-chip-label">People</span>
          </div>
          <div className="sla-summary-chip">
            <span className="sla-summary-chip-value">{totals.active}</span>
            <span className="sla-summary-chip-label">Active</span>
          </div>
          <div className="sla-summary-chip">
            <span className="sla-summary-chip-value">{totals.open}</span>
            <span className="sla-summary-chip-label">Open</span>
          </div>
          <div className="sla-summary-chip">
            <span className="sla-summary-chip-value">{totals.inProgress}</span>
            <span className="sla-summary-chip-label">In progress</span>
          </div>
          <div className="sla-summary-chip">
            <span className="sla-summary-chip-value">{totals.closed}</span>
            <span className="sla-summary-chip-label">Closed</span>
          </div>
          <div className="sla-summary-chip">
            <span className="sla-summary-chip-value">{totals.total}</span>
            <span className="sla-summary-chip-label">Total tickets</span>
          </div>
        </div>

        <div className="wd-chart-card wd-soft-card is-auto-height wd-perf-card" style={{ margin: 0 }}>
          <div className="wd-perf-head">
            <Lightning size={18} weight="fill" className="wd-perf-icon" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p className="wd-chart-title">Assignee leaderboard</p>
              <p className="wd-chart-subtitle">
                Closed-first ranking across all products · {ranked.length} shown
              </p>
            </div>
            <form
              className="sla-api-search"
              style={{ margin: 0, maxWidth: 280 }}
              onSubmit={(e) => e.preventDefault()}
              role="search"
            >
              <MagnifyingGlass size={16} className="sla-api-search-icon" aria-hidden />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, email…"
              />
            </form>
          </div>

          {ranked.length === 0 ? (
            <EmptyState
              icon={User}
              title={q ? 'No matches' : 'No performance data'}
              description={q ? `No assignees match “${q}”.` : 'No assignee ticket activity yet.'}
            />
          ) : (
            <div className="wd-chart-body" style={{ maxHeight: 'none' }}>
              <div className="wd-perf-list">
                {ranked.map((row, index) => {
                  const closedPct = Math.round(((row.closed || 0) / ceiling) * 100);
                  const activePct = Math.min(
                    100,
                    Math.round(((row.active || 0) / Math.max(ceiling, 1)) * 100),
                  );
                  return (
                    <div
                      key={row.user_id}
                      className={`wd-perf-row${mounted ? ' is-in' : ''}`}
                      style={{ animationDelay: `${Math.min(index, 20) * 40}ms`, cursor: 'default' }}
                    >
                      <span className="wd-perf-rank">{index + 1}</span>
                      <div className="wd-perf-body" style={{ flex: 1, minWidth: 0 }}>
                        <div className="wd-perf-meta">
                          <div style={{ minWidth: 0 }}>
                            <button
                              type="button"
                              className="wd-perf-name-btn"
                              onClick={() => open360(row.user_id, 'active')}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                padding: 0,
                                font: 'inherit',
                                fontWeight: 700,
                                color: '#111827',
                                cursor: 'pointer',
                                textAlign: 'left',
                              }}
                            >
                              {row.name}
                            </button>
                            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>
                              {[row.designation, row.email].filter(Boolean).join(' · ') || row.user_id}
                            </div>
                          </div>
                          <span className="wd-perf-stats" style={{ flexWrap: 'wrap', gap: 6 }}>
                            <StatPill label="Closed" value={row.closed || 0} onClick={() => open360(row.user_id, 'closed')} />
                            <StatPill label="Active" value={row.active || 0} onClick={() => open360(row.user_id, 'active')} />
                            <StatPill label="Open" value={row.open || 0} onClick={() => open360(row.user_id, 'open')} />
                            <StatPill label="In prog" value={row.in_progress || 0} onClick={() => open360(row.user_id, 'active')} />
                            <StatPill label="Total" value={row.total || 0} onClick={() => open360(row.user_id, 'total')} />
                          </span>
                        </div>
                        <div className="wd-perf-tracks" style={{ marginTop: 8 }}>
                          <div className="wd-perf-track wd-perf-track-closed" aria-hidden>
                            <div
                              className="wd-perf-fill wd-perf-fill-closed"
                              style={{ width: mounted ? `${closedPct}%` : '0%' }}
                            />
                          </div>
                          <div className="wd-perf-track wd-perf-track-active" aria-hidden>
                            <div
                              className="wd-perf-fill wd-perf-fill-active"
                              style={{ width: mounted ? `${activePct}%` : '0%' }}
                            />
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 11, color: '#9CA3AF' }}>
                          <span>Closed share {closedPct}%</span>
                          <span>Active vs peak {activePct}%</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="wd-product-team-pipeline"
                        onClick={() => open360(row.user_id, 'active')}
                        style={{ flexShrink: 0, alignSelf: 'center' }}
                      >
                        360°
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </ModuleShell>
  );
}

function StatPill({ label, value, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: '1px solid #E5E7EB',
        background: '#FFFFFF',
        borderRadius: 8,
        padding: '4px 8px',
        fontSize: 11,
        cursor: 'pointer',
        color: '#374151',
      }}
    >
      <strong style={{ color: '#111827' }}>{value}</strong>
      {' '}
      {label}
    </button>
  );
}
